import React, { useEffect, useState, useRef, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from './messages'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import AsyncSelect from 'react-select/async'
import debounce from 'debounce-promise'
import { createPortal } from 'react-dom'
import Select, { components } from 'react-select'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import useCurrentUser from 'hooks/useCurrentUser'

const Control = (props) => (
  <components.Control
    {...props}
    className={classNames('border px-4 h-full', {
      'border-gray-400': props.isFocused,
      'border-primary-darker': !props.isFocused,
    })}
  />
)

const Placeholder = ({ children, className, isFocused, ...props }) => (
  <components.Placeholder className={classNames(classNames, 'flex w-full')} {...props} isFocused={isFocused}>
    <div className="grow truncate">
      { children }
    </div>

    <kbd className={classNames('shrink-0 rounded px-1 mr-3', {
      'hidden md:inline-block bg-primary': !isFocused,
      'hidden': isFocused,
    })}>/</kbd>
  </components.Placeholder>
)

const IndicatorSeparator = () => null
const DropdownIndicator = () => null

const Option = ({ children, ...props }) => {
  return (
    <components.Option {...props}>
      { props.data?.meta &&
        <span className="w-full md:w-24 inline-block">
          <span className={classNames('font-medium text-xs text-white rounded px-2 py-1 mr-2', {
            CUSTOMER: 'bg-green-400',
            SUPPLIER: 'bg-blue-400',
            COORDINATOR: 'bg-red-400',
            ORDER: 'bg-purple-400',
            TASK: 'bg-blue-400',
            EXCHANGE: 'bg-rose-400',
            DEMO: 'bg-yellow-400',
          }[props.data.meta.type] ?? '')}>
            { props.data.meta.type }
          </span>
        </span>
      }
      <span>
        { children }
      </span>
    </components.Option>
  )
}

const CommandPalette = ({ inline = false }) => {
  const selectRef = useRef()
  const [subCommands, setSubCommands] = useState(false)
  const [show, setShow] = useState(inline)
  const [value, setValue] = useState(false)
  const { formatMessage } = useIntl()
  const history = useHistory()
  const { track, impersonate } = useCurrentUser()

  useKeyboardShortcut(['Shift', '/'], () => {
    setShow(true)
    selectRef.current.select.onMenuOpen()
    selectRef.current.select.focus()
  }, {
    afterCallback: (keys) => track.increment('global.command-palette.open', { kbd: Object.keys(keys).join('+') })
  })

  const onClose = () => {
    setShow(false)
    selectRef.current.select.onMenuClose()
  }

  const afterCallback = (keys) => track.increment('global.command-palette.close', { kbd: Object.keys(keys).join('+') })

  useKeyboardShortcut(['Escape'], onClose, { afterCallback })
  useKeyboardShortcut(['Esc'], onClose, { afterCallback })

  const onChange = (value) => {
    setValue(value)

    const subCommands = (value && value.meta) ? {
      SUPPLIER: [
        { label: 'Go', value: 'go', func: () => history.push(value.meta?.path) },
        { label: 'Impersonate', value: 'impersonate', func: () => impersonate(value.meta?.contactId) },
      ],
      COORDINATOR: [
        { label: 'Impersonate', value: 'impersonate', func: () => impersonate(value.value) },
      ],
      EXCHANGE: [
        {
          label: 'Copy to clipboard',
          value: 'copy',
          func: () => window.navigator.clipboard.writeText(value.value).then(() => {
            window.toastr.info('Copied to clipboard')
          })
        }
      ],
    }[value.meta.type] : null;


    if (value?.func) {
      setShow(false)
      setSubCommands([])
      selectRef.current.select.onMenuClose()
      setValue(null)
      value.func()
      track.increment('global.command-palette.cta', {
        type: value.meta?.type,
        action: value.value,
      })
    } else if (subCommands) {
      setSubCommands(subCommands)
      selectRef.current.select.onMenuOpen()
      track.increment('global.command-palette.cta', {
        type: value.meta?.type,
        action: 'subc',
      })
    } else {
      history.push(value.meta?.path)
    }

    selectRef.current.select.focus()
  }

  const renderSelect = useCallback(() => (
    <AsyncSelect
      components={{ Control, Option, Placeholder, IndicatorSeparator, DropdownIndicator }}
      styles={{
        control: (base, state) => ({
          ...base,
          background: state.isFocused ? '#FFFFFF' : 'var(--color-primary-darker)',
          borderColor: state.isFocused ? '#FFFFFF' : 'var(--color-primary-darker)',
        }),
        placeholder: (base, state) => ({
          ...base,
          color: state.isFocused ? '#52525b' : 'var(--color-primary-lighter)',
        })
      }}
      value={value}
      onChange={onChange}
      cacheOptions
      loadOptions={debounce((query) => {
        return new Promise((resolve, reject) => {
          fetch(`/system/search?query=${query}`)
            .then(response => response.json())
            .then(resolve)
            .catch(reject)
        })
      }, 250)}
      defaultOptions={subCommands}
      placeholder="Search orders, deal IDs, tasks, suppliers, customers, etc."
      className="w-full"
      isClearable
      autoFocus={!inline}
      openMenuOnFocus
      ref={selectRef}
    />
  ), [subCommands, selectRef, onChange, value])

  if (inline) {
    return renderSelect()
  }

  if (!show) {
    return null
  }

  return createPortal(
    <div className="fixed z-10 inset-0 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen text-center sm:block">
        <div className="fixed inset-0 transition-opacity">
          <div className="absolute inset-0 bg-gray-200 opacity-75" />
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-sm text-left shadow transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
          { renderSelect() }
        </div>
      </div>
    </div>
  , document.getElementById('react_app_modal'))
}

export default CommandPalette
